/**
 * The invitation funnel: invited → opened → started → finished → saw the
 * cross-sell → became a member.
 *
 * Hand-rolled MUI boxes, like StatusFunnelCard and ConversionFunnelCard next to
 * it. Six proportional bars are layout, not plotting, and this way every colour
 * is a theme palette reference and the caption for the one dishonest step can
 * sit inside the row it belongs to.
 *
 * ⚠️ ONE STEP IS NOT MEASURED THE SAME WAY AS THE OTHERS.
 * `wedding_invitations.opened_at` has never had a writer, so "Opened" is a
 * Brevo TAG-LEVEL AGGREGATE for the invitation and 48h-reminder templates, not
 * a count of these guests. It is drawn differently, captioned as such, and
 * excluded from the biggest-drop calculation — the server computes that over
 * the per-guest steps only, because a step measured another way cannot be
 * differenced against its neighbours. A click is never treated as an open.
 *
 * When the provider table is absent the step's value is NULL and renders '—',
 * never 0: a zero here would claim a measured nothing.
 */

import React from 'react';
import { Box, Card, CardContent, Chip, LinearProgress, Stack, Tooltip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { num, percentage } from '../weddingFormat';
import type { InvitationFunnel, InvitationFunnelStep } from '../weddingApi';

interface Props {
  funnel: InvitationFunnel | undefined;
  rangeLabel: string;
}

const PROVIDER_CAPTION =
  'Provider-level aggregate, not per guest. Opens are reported by Brevo against the invitation and '
  + 'reminder template tags across the whole account, so this number is not a subset of the row above '
  + 'it and the two cannot be divided by each other. Nothing infers an open from a click.';

/** The last step is the outcome, so it gets the accent the rest do not. */
function toneFor(step: InvitationFunnelStep, isLast: boolean): 'primary' | 'secondary' | 'error' {
  if (isLast) return 'error';
  if (step.measurement === 'provider_aggregate') return 'secondary';
  return 'primary';
}

const InvitationFunnelCard: React.FC<Props> = ({ funnel, rangeLabel }) => {
  const steps = funnel?.steps || [];
  const base = steps.length > 0 ? num(steps[0].value) : 0;

  const anyMeasured = steps.some((s) => s.value !== null && s.value !== undefined && num(s.value) > 0);

  const labelFor = (key: string) => steps.find((s) => s.key === key)?.label || key;

  return (
    <Card sx={{ height: '100%', borderRadius: '16px' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Invitation funnel
          </Typography>
          <Chip size="small" color="primary" variant="outlined" label={rangeLabel} />
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Every guest invited across every book.
        </Typography>

        {steps.length === 0 || !anyMeasured ? (
          <Typography variant="body2" color="text.secondary">
            No invitation was sent in this range.
          </Typography>
        ) : (
          <Stack spacing={1.75}>
            {steps.map((step, index) => {
              const isLast = index === steps.length - 1;
              const missing = step.value === null || step.value === undefined;
              const pct = missing ? 0 : percentage(step.value, base);
              const provider = step.measurement === 'provider_aggregate';
              return (
                <Box key={step.key}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Typography variant="body2">{step.label}</Typography>
                      {provider && (
                        <Tooltip title={PROVIDER_CAPTION}>
                          <InfoOutlinedIcon fontSize="small" color="secondary" />
                        </Tooltip>
                      )}
                    </Stack>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {missing ? '—' : num(step.value).toLocaleString()}
                      {!missing && !provider && base > 0 && (
                        <Typography
                          component="span"
                          variant="caption"
                          color="text.secondary"
                          sx={{ ml: 0.5 }}
                        >
                          ({pct}%)
                        </Typography>
                      )}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(100, pct)}
                    color={toneFor(step, isLast)}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      // A provider aggregate is drawn hollow so it cannot be
                      // read as another slice of the same population.
                      ...(provider ? { opacity: 0.55 } : {}),
                    }}
                    aria-label={`${step.label}: ${missing ? 'not measured' : num(step.value)}`}
                  />
                </Box>
              );
            })}
          </Stack>
        )}

        {/*
          The Opened caveat is stated in PROSE as well as in the tooltip. An
          operator scanning the card must not have to hover to learn that one of
          the six numbers is a different kind of thing.
        */}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
          {funnel?.opens_available
            ? 'Opened is a provider-level aggregate for the invitation and reminder tags — not per guest, and not a subset of Invited.'
            : 'Opens are not available in this database: they come from the email provider’s daily statistics, which are not present here. The step reads “—” rather than zero.'}
        </Typography>

        {funnel?.biggest_drop ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            <strong>Biggest drop:</strong> {labelFor(funnel.biggest_drop.from).toLowerCase()} →{' '}
            {labelFor(funnel.biggest_drop.to).toLowerCase()} — {funnel.biggest_drop.lost.toLocaleString()}{' '}
            {funnel.biggest_drop.lost === 1 ? 'guest' : 'guests'} ({funnel.biggest_drop.pct}%) did not carry
            through. Measured across the per-guest steps only.
          </Typography>
        ) : (
          anyMeasured && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
              No step lost guests in this range.
            </Typography>
          )
        )}

        {funnel?.started_definition && (
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1 }}>
            “Started writing” is {funnel.started_definition}.
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default InvitationFunnelCard;
